import { useEffect, useRef } from 'react';

import type {
  UseQueryResult,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { SnackbarType } from '@/contexts/Snackbar/SnackbarType';
import { isAuthInteractionRequiredError } from '@/errors/AuthInteractionRequiredError';
import type { NetworkError } from '@/errors/NetworkError';
import useRouter from '@/hooks/useRouter';
import useSnackbar from '@/hooks/useSnackbar';
import useTranslation from '@/hooks/useTranslation';

export const SESSION_EXPIRED_REDIRECT_DELAY_MS = 2_000;

interface SessionExpiryOwner {
  redirect: () => void;
}

/**
 * A page mounts several authenticated queries, and they all fail together
 * when the session expires. This page-wide coordinator shows one notice and
 * performs one redirect, cancelled only when every failing query recovers.
 */
const sessionExpiryOwners = new Map<symbol, SessionExpiryOwner>();
let sessionExpiryTimeoutId: number | undefined;

function clearSessionExpiryTimeout(): void {
  if (sessionExpiryTimeoutId !== undefined) {
    window.clearTimeout(sessionExpiryTimeoutId);
    sessionExpiryTimeoutId = undefined;
  }
}

function redirectForExpiredSession(): void {
  clearSessionExpiryTimeout();
  const owners = [...sessionExpiryOwners.values()];
  sessionExpiryOwners.clear();
  owners.at(-1)?.redirect();
}

function registerSessionExpiry({
  ownerId,
  owner,
  notify,
  immediate,
}: {
  ownerId: symbol;
  owner: SessionExpiryOwner;
  notify: () => void;
  immediate: boolean;
}): void {
  const isFirstOwner =
    sessionExpiryOwners.size === 0 && sessionExpiryTimeoutId === undefined;
  sessionExpiryOwners.set(ownerId, owner);

  if (isFirstOwner) {
    notify();
  }

  if (immediate) {
    redirectForExpiredSession();
    return;
  }

  if (isFirstOwner) {
    sessionExpiryTimeoutId = window.setTimeout(
      redirectForExpiredSession,
      SESSION_EXPIRED_REDIRECT_DELAY_MS
    );
  }
}

function releaseSessionExpiry(ownerId: symbol): void {
  if (!sessionExpiryOwners.delete(ownerId)) return;
  if (sessionExpiryOwners.size === 0) {
    clearSessionExpiryTimeout();
  }
}

/** Test-only: production state resets when the redirect runs. */
export function resetSessionExpiryRedirectForTests(): void {
  clearSessionExpiryTimeout();
  sessionExpiryOwners.clear();
}

function useGenericSideEffects<Response>({
  automaticRedirect,
  query,
}: {
  query:
    | UseSuspenseQueryResult<Response, NetworkError>
    | UseQueryResult<Response, NetworkError>;
  automaticRedirect: boolean;
}) {
  const router = useRouter();
  const { open: openSnackbar } = useSnackbar();
  const t = useTranslation('common');
  const ownerId = useRef<symbol | null>(null);
  const routerRef = useRef(router);

  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  useEffect(
    () => () => {
      if (ownerId.current) {
        releaseSessionExpiry(ownerId.current);
        ownerId.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const isUnauthorized = query.error?.statusCode === 401;
    if (!automaticRedirect || !isUnauthorized) {
      if (ownerId.current) {
        releaseSessionExpiry(ownerId.current);
        ownerId.current = null;
      }
      return;
    }

    if (ownerId.current) {
      return;
    }

    const id = Symbol('session-expiry-owner');
    ownerId.current = id;
    registerSessionExpiry({
      ownerId: id,
      // redirectToLogin keeps the current page as the post-login target.
      owner: { redirect: () => routerRef.current.redirectToLogin() },
      notify: () =>
        openSnackbar({
          message: t('session.expired-message'),
          type: SnackbarType.Warning,
        }),
      immediate: isAuthInteractionRequiredError(query.error),
    });
  }, [automaticRedirect, openSnackbar, query.error, t]);
}

export default useGenericSideEffects;
